<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <oxd-table-filter
    :filter-title="$t('attendance.employee_attendance_records')"
  >
    <oxd-form @submit-valid="filterItems">
      <oxd-form-row>
        <oxd-grid :cols="4" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <employee-autocomplete
              v-model="filters.employee"
              :params="{
                includeEmployees: 'currentAndPast',
              }"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              v-model="filters.fromDate"
              :rules="rules.fromDate"
              :label="$t('general.from')"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              v-model="filters.toDate"
              :rules="rules.toDate"
              :label="$t('general.to')"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>

      <oxd-divider />

      <oxd-form-actions>
        <required-text />
        <div class="oxd-export-btn">
          <oxd-button
            display-type="success"
            :label="$t('general.export_to_excel')"
            type="submit"
            @click="exportToExcel"
          />
        </div>
        <oxd-button
          display-type="secondary"
          :label="$t('general.view')"
          type="submit"
        />
      </oxd-form-actions>
    </oxd-form>
  </oxd-table-filter>
  <br />
  <div class="orangehrm-paper-container">
    <table-header
      :total="total"
      :selected="0"
      :loading="isLoading"
      :show-divider="false"
    ></table-header>
    <div class="orangehrm-container">
      <oxd-card-table
        :headers="headersf.headers"
        :items="items?.data"
        :selectable="false"
        :clickable="false"
        :loading="isLoading"
        row-decorator="oxd-table-decorator-card"
      />
    </div>
    <div class="orangehrm-bottom-container">
      <oxd-pagination
        v-if="showPaginator"
        v-model:current="currentPage"
        :length="pages"
      />
    </div>
  </div>
</template>

<script>
import {computed, ref} from 'vue';
import {validDateFormat} from '@/core/util/validation/rules';
import {navigate} from '@/core/util/helper/navigation';
import {APIService} from '@/core/util/services/api.service';
import usePaginate, {
  getQueryParams,
} from '@ohrm/core/util/composable/usePaginate';
import {freshDate, formatDate} from '@ohrm/core/util/helper/datefns';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import {yearRange} from '@/core/util/helper/year-range';
import useDateFormat from '@/core/util/composable/useDateFormat';
import useEmployeeNameTranslate from '@/core/util/composable/useEmployeeNameTranslate';
import dayjs from 'dayjs';

export default {
  components: {
    'employee-autocomplete': EmployeeAutocomplete,
  },

  props: {
    fromDate: {
      type: String,
      default: null,
    },
    toDate: {
      type: String,
      default: null,
    },
  },

  setup(props) {
    const {$tEmpName} = useEmployeeNameTranslate();
    const {userDateFormat} = useDateFormat();

    const rules = {
      fromDate: [validDateFormat(userDateFormat)],
      toDate: [validDateFormat(userDateFormat)],
    };

    const filters = ref({
      fromDate: props.fromDate
        ? props.fromDate
        : formatDate(freshDate(), 'yyyy-MM-dd'),
      toDate: props.toDate
        ? props.toDate
        : formatDate(freshDate(), 'yyyy-MM-dd'),
      employee: null,
      action: 'view', // view or export
    });

    const serializedFilters = computed(() => {
      return {
        toDate: filters.value.toDate,
        fromDate: filters.value.fromDate,
        empNumber: filters.value.employee?.id,
      };
    });

    const attendanceRecordNormalizer = (data) => {
      return data.map((item) => {
        const records = item.records || [];
        const recordData = {};

        for (const record of records) {
          const startDate = dayjs(record.punchInSystem);
          const dateAt = startDate.format('YYYY-MM-DD');
          const label = `date:${dateAt}`;

          const startHours = startDate.format('HH:mm');
          let endHours = 'N\\A';

          if (record.punchOutSystem) {
            const endDate = dayjs(record.punchOutSystem);
            endHours = endDate.format('HH:mm');
          }

          if (!recordData[label]) {
            recordData[label] = `${startHours} - ${endHours}`;
          } else {
            recordData[
              label
            ] = `${recordData[label]} \n ${startHours} - ${endHours}`;
          }
        }

        return {
          id: item.empNumber,
          employeeId: item.employeeId,
          empName: $tEmpName(item, {
            excludePastEmpTag: false,
          }),
          ...recordData,
          duration: item.sum?.label,
        };
      });
    };

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/attendance/employees/summary',
    );

    const exportExecel = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/attendance/employees/summary/export',
    );

    const {
      total,
      pages,
      response,
      isLoading,
      execQuery: pExecQuery,
      currentPage,
      showPaginator,
    } = usePaginate(http, {
      query: serializedFilters,
      normalizer: attendanceRecordNormalizer,
    });

    const execQuery = () => {
      if (filters.value.action === 'export') {
        const queryParams = getQueryParams(serializedFilters);

        exportExecel
          .getAll({
            ...queryParams,
          })
          .then((res) => {
            const link = document.createElement('a');
            const fromDate =
              filters.value.fromDate instanceof Date
                ? dayjs(filters.value.fromDate).format('YYYY-MM-DD')
                : filters.value.fromDate;
            const toDate =
              filters.value.toDate instanceof Date
                ? dayjs(filters.value.toDate).format('YYYY-MM-DD')
                : filters.value.toDate;
            link.href = res.data.data.url;
            link.setAttribute(
              'download',
              `employee-attendances-${fromDate}_${toDate}.xlsx`,
            );
            document.body.appendChild(link);
            link.click();

            this.$toast.success({
              title: 'Exporting Data',
              message: 'Data Exported Successfully',
            });
          })
          .catch(() => {
            this.$toast.error({
              title: 'Exporting Data',
              message: 'Data Export Failed ',
            });
          });
        return;
      } else {
        pExecQuery();
      }
    };

    return {
      http,
      rules,
      total,
      pages,
      filters,
      isLoading,
      execQuery,
      currentPage,
      showPaginator,
      items: response,
    };
  },

  data() {
    const startFilter = this.filters.fromDate;
    const endFilter = this.filters.toDate;
    const dateIns = [];

    for (
      let d = new Date(startFilter);
      d <= new Date(endFilter);
      d.setDate(d.getDate() + 1)
    ) {
      const mDate = dayjs(d).format('YYYY-MM-DD');
      const title = dayjs(d).format('DD MMM');
      dateIns.push({
        name: `date:${mDate}`,
        title: title,
        style: {flex: '20%'},
      });
    }
    return {
      yearArray: [...yearRange()],
      headers: [
        {
          name: 'employeeId',
          title: 'Employee ID',
          style: {flex: '10%'},
        },
        {
          name: 'empName',
          slot: 'title',
          title: this.$t('general.employee_name'),
          style: {flex: '40%'},
        },
        ...dateIns,
        {
          name: 'duration',
          title: this.$t('time.total_duration'),
          style: {flex: '10%'},
        },
        {
          name: 'actions',
          slot: 'footer',
          title: this.$t('general.actions'),
          style: {flex: '20%'},
          cellType: 'oxd-table-cell-actions',
          cellConfig: {
            view: {
              onClick: this.onClickView,
              component: 'oxd-button',
              props: {
                label: this.$t('general.view'),
                displayType: 'text',
                size: 'medium',
              },
            },
          },
        },
      ],
    };
  },

  computed: {
    headersf: {
      get() {
        const startFilter = this.filters.fromDate;
        const endFilter = this.filters.toDate;
        const dateIns = [];

        for (
          let d = new Date(startFilter);
          d <= new Date(endFilter);
          d.setDate(d.getDate() + 1)
        ) {
          const mDate = dayjs(d).format('YYYY-MM-DD');
          const title = dayjs(d).format('DD MMM');
          dateIns.push({
            name: `date:${mDate}`,
            title: title,
            style: {flex: '20%'},
          });
        }

        return {
          yearArray: [...yearRange()],
          headers: [
            {
              name: 'employeeId',
              title: 'Employee ID',
              style: {flex: '10%'},
            },
            {
              name: 'empName',
              slot: 'title',
              title: this.$t('general.employee_name'),
              style: {flex: '40%'},
            },
            {
              name: 'duration',
              title: this.$t('time.total_duration'),
              style: {flex: '40%'},
            },
            {
              name: 'actions',
              slot: 'footer',
              title: this.$t('general.actions'),
              style: {flex: '20%'},
              cellType: 'oxd-table-cell-actions',
              cellConfig: {
                view: {
                  onClick: this.onClickView,
                  component: 'oxd-button',
                  props: {
                    label: this.$t('general.view'),
                    displayType: 'text',
                    size: 'medium',
                  },
                },
              },
            },
          ],
        };
      },
    },
  },

  methods: {
    async resetDataTable() {
      await this.execQuery();
    },
    async exportToExcel(event) {
      // stop event propagation
      event.stopPropagation();
      event.preventDefault();

      this.filters.action = 'export';
      try {
        await this.execQuery();
      } finally {
        this.filters.action = 'view';
      }
    },
    async filterItems() {
      // if (this.filters.employee && this.filters.date) {
      //   return navigate('/attendance/viewAttendanceRecord', undefined, {
      //     employeeId: this.filters.employee.id,
      //     toDate: this.filters.toDate,
      //     fromDate: this.filters.fromDate,
      //   });
      // }
      // await this.execQuery();
      this.onClickView();
    },
    onClickView() {
      navigate('/attendance/viewAttendanceRecord', undefined, {
        employeeId: this.filters.employee.id,
        toDate: this.filters.toDate,
        fromDate: this.filters.fromDate,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep(.card-footer-slot) {
  .oxd-table-cell-actions {
    justify-content: flex-end;
  }

  .oxd-table-cell-actions > * {
    margin: 0 !important;
  }
}

.oxd-export-btn {
  margin-right: 4px;
}
</style>
